// Tamil-Katral-Maiyam\src\pages\admin\ParentsList.tsx

import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { formatDate } from '../../utils/format';
import {
  validateEmail,
  validatePersonName,
  stripDigitsFromName,
} from '../../utils/validation';
import {
  listParents,
  createParent,
  CreateParentPayload,
  RosterParent,
  inviteParent,
  InviteParentPayload,
  listStudents,
  RosterStudent,
} from '../../modules/roster/api/roster.client';
import { Eye, UserPlus } from 'lucide-react';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const EMAIL_MAX_LENGTH = 100;
const NOTES_MAX_LENGTH = 150;

export const ParentsList: React.FC = () => {
  const [parents, setParents] = useState<RosterParent[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailParent, setDetailParent] = useState<RosterParent | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<RosterParent | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteParentPayload>({
    email: '',
    invitee_name: '',
    notes: '',
    student_id: '',
    metadata: {
      relationship: '',
    },
  });
  const [parentForm, setParentForm] = useState<CreateParentPayload>({
    personal: {
      display_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    },
    notes: '',
  });
  const [addParentErrors, setAddParentErrors] = useState<Record<string, string>>({});
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createdFromDate, setCreatedFromDate] = useState('');

  const filteredParents = useMemo(() => {
    return parents.filter((p) => {
      const search = searchText.trim().toLowerCase().replace(/\s/g, '');
      if (search) {
        const displayName = (p.personal?.display_name || '').toLowerCase();
        const firstName = (p.personal?.first_name || '').toLowerCase();
        const lastName = (p.personal?.last_name || '').toLowerCase();
        const email = (p.personal?.email || '').toLowerCase();
        const phone = (p.personal?.phone || '').replace(/\s/g, '');
        const matchSearch =
          displayName.includes(search) ||
          firstName.includes(search) ||
          lastName.includes(search) ||
          email.includes(search) ||
          phone.includes(search);
        if (!matchSearch) return false;
      }
      if (statusFilter && (p.status || '') !== statusFilter) return false;
      if (createdFromDate && p.created_at) {
        const created = p.created_at.slice(0, 10);
        if (created < createdFromDate) return false;
      }
      return true;
    });
  }, [parents, searchText, statusFilter, createdFromDate]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    parents.forEach((p) => {
      if (p.status) set.add(p.status);
    });
    return [
      { value: '', label: 'All statuses' },
      ...Array.from(set).map((v) => ({ value: v, label: v })),
    ];
  }, [parents]);

  const hasActiveFilters = Boolean(searchText.trim() || statusFilter || createdFromDate);
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setCreatedFromDate('');
  };

  // ✅ Placeholders for ALL fields (keeps same structure)
  const placeholders = useMemo(
    () => ({
      parent_display_name: 'Enter your name',
      parent_first_name: 'Enter your first name',
      parent_last_name: 'Enter your last name',
      parent_email: 'name@gmail.com',
      parent_phone: 'Enter your phone number',
      parent_notes: 'Enter notes (optional)',

      invite_email: 'name@gmail.com',
      invitee_name: 'Enter your name',
      invite_student: 'Select a student...',
      invite_relationship: 'Select relationship...',
      invite_notes: 'Enter notes (optional)',
    }),
    []
  );

  const loadParents = async () => {
    setLoading(true);
    try {
      const items = await listParents({ limit: 50, skip: 0 });
      setParents(items);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParents();
  }, []);

  const loadStudents = async () => {
    setStudentsLoading(true);
    try {
      const items = await listStudents({ limit: 100, skip: 0 });
      setStudents(Array.isArray(items) ? items : []);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    if (isInviteModalOpen) {
      loadStudents();
    }
  }, [isInviteModalOpen]);

  const openDetail = async (parentId: string) => {
    setDetailLoading(true);
    try {
      const data = parents.find((p) => p.id === parentId);
      if (data) {
        setDetailParent(data);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const detailRows = useMemo(() => {
    if (!detailParent) return [];

    // Fields to exclude (IDs and internal fields)
    const excludeFields = [
      'id',
      'tenant_id',
      'created_by',
      'updated_by',
      'deleted_by',
      'invite_id',
      'personal',
      'invited_at',
      'accepted_at',
      // hide these fields in detail view per request
      'platform_user_id',
      'status',
      'created_at',
      'updated_at',
      'deleted_at',
    ];

    const rows: Array<{ label: string; value: string }> = [];

    // Add personal fields individually
    if (detailParent.personal) {
      rows.push(
        { label: 'First Name', value: detailParent.personal.first_name || '-' },
        { label: 'Last Name', value: detailParent.personal.last_name || '-' },
        { label: 'Email', value: detailParent.personal.email || '-' },
        { label: 'Phone', value: detailParent.personal.phone || '-' }
      );
    }

    // Add other fields
    Object.entries(detailParent)
      .filter(([key]) => !excludeFields.includes(key))
      .forEach(([key, value]) => {
        const formattedLabel = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        let formattedValue: string;

        if (value === null || value === undefined) {
          formattedValue = '-';
        } else if (Array.isArray(value)) {
          formattedValue = value.join(', ');
        } else if (typeof value === 'object') {
          formattedValue = JSON.stringify(value, null, 2);
        } else {
          formattedValue = String(value);
        }

        rows.push({ label: formattedLabel, value: formattedValue });
      });

    return rows;
  }, [detailParent]);

  const getInviteStatus = (parent: RosterParent) => {
    if (parent.platform_user_id || parent.accepted_at) return 'Active';
    if (parent.invited_at) return 'Invited';
    return 'Draft';
  };

  const canInvite = (parent: RosterParent) => {
    return !parent.platform_user_id && parent.status !== 'active';
  };

  const openInviteModal = (parent: RosterParent) => {
    setInviteTarget(parent);
    setInviteForm({
      email: parent.personal?.email || '',
      invitee_name:
        parent.personal?.display_name ||
        `${parent.personal?.first_name || ''} ${parent.personal?.last_name || ''}`.trim(),
      notes: '',
      student_id: '',
      metadata: {
        relationship: '',
      },
    });
    setIsInviteModalOpen(true);
  };

  const handleInviteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!inviteTarget) {
      showErrorToast('Parent not selected');
      return;
    }
    if (!inviteForm.email.trim() || !inviteForm.invitee_name.trim() || !inviteForm.student_id.trim()) {
      showErrorToast('Email, invitee name, and student are required');
      return;
    }
    setIsInviting(true);
    try {
      await inviteParent(inviteTarget.id, {
        email: inviteForm.email,
        invitee_name: inviteForm.invitee_name,
        notes: inviteForm.notes || undefined,
        student_id: inviteForm.student_id,
        metadata: inviteForm.metadata?.relationship
          ? { relationship: inviteForm.metadata.relationship }
          : undefined,
      });
      showSuccessToast('Parent invited');
      setIsInviteModalOpen(false);
      setInviteTarget(null);
      setInviteForm({
        email: '',
        invitee_name: '',
        notes: '',
        student_id: '',
        metadata: { relationship: '' },
      });
      await loadParents();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsInviting(false);
    }
  };

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddParentErrors({});
    const errors: Record<string, string> = {};
    const firstName = (parentForm.personal.first_name ?? '').trim();
    const lastName = (parentForm.personal.last_name ?? '').trim();

    if (!firstName) {
      errors.first_name = 'First name is required';
    } else {
      const r = validatePersonName(parentForm.personal.first_name ?? '');
      if (!r.valid) errors.first_name = 'First name should not contain numbers';
    }
    if (lastName) {
      const r = validatePersonName(parentForm.personal.last_name ?? '');
      if (!r.valid) errors.last_name = 'Last name should not contain numbers';
    }

    const emailTrim = (parentForm.personal.email ?? '').trim();
    if (!emailTrim) {
      errors.email = 'Email is required';
    } else {
      if (emailTrim.length > EMAIL_MAX_LENGTH) {
        errors.email = `Email must be at most ${EMAIL_MAX_LENGTH} characters`;
      } else if (!validateEmail(emailTrim)) {
        errors.email = 'Enter a valid email address';
      }
    }

    if (parentForm.personal.phone && /[a-zA-Z]/.test(parentForm.personal.phone)) {
      errors.phone = 'Phone should contain only numbers and country code';
    }

    const notes = parentForm.notes ?? '';
    if (notes.length > NOTES_MAX_LENGTH) {
      errors.notes = `Notes must be at most ${NOTES_MAX_LENGTH} characters`;
    }

    if (Object.keys(errors).length > 0) {
      setAddParentErrors(errors);
      const firstMessage = Object.values(errors)[0];
      if (firstMessage) showErrorToast(firstMessage);
      return;
    }

    const display_name = [parentForm.personal.first_name, parentForm.personal.last_name]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    setIsCreating(true);
    try {
      await createParent({
        personal: {
          display_name,
          first_name: parentForm.personal.first_name,
          last_name: parentForm.personal.last_name || '',
          email: parentForm.personal.email,
          phone: parentForm.personal.phone || null,
        },
        notes: (parentForm.notes ?? '').slice(0, NOTES_MAX_LENGTH) || null,
      });
      showSuccessToast('Parent created');
      setAddModalOpen(false);
      setAddParentErrors({});
      setParentForm({
        personal: {
          display_name: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
        },
        notes: '',
      });
      await loadParents();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Parents"
        subtitle="Manage parent profiles"
        actions={<Button onClick={() => setAddModalOpen(true)}>Add Parent</Button>}
      />

      <Card>
        <CardContent padding="md">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center mb-4">
            <div className="md:col-span-6">
              <Input
                placeholder="Search by name, email, phone..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            <div className="md:col-span-3 w-full">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11"
              />
            </div>
            <div className="md:col-span-3">
              <Input
                type="date"
                value={createdFromDate}
                onChange={(e) => setCreatedFromDate(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            {hasActiveFilters && (
              <div className="col-span-1 md:col-span-12 flex md:justify-end">
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invite Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredParents.map((parent) => (
                  <TableRow key={parent.id} onClick={() => openDetail(parent.id)}>
                    <TableCell className="font-medium">
                      {parent.personal?.display_name ||
                        `${parent.personal?.first_name || ''} ${parent.personal?.last_name || ''}`.trim() ||
                        '-'}
                    </TableCell>
                    <TableCell>{parent.personal?.email || '-'}</TableCell>
                    <TableCell>{parent.personal?.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={parent.status === 'active' ? 'success' : 'gray'}>
                        {parent.status || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          getInviteStatus(parent) === 'Active'
                            ? 'success'
                            : getInviteStatus(parent) === 'Invited'
                            ? 'warning'
                            : 'gray'
                        }
                      >
                        {getInviteStatus(parent)}
                      </Badge>
                    </TableCell>
                    <TableCell>{parent.created_at ? formatDate(parent.created_at) : '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5 h-9 w-9 rounded-xl flex items-center justify-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(parent.id);
                          }}
                          aria-label="View parent"
                          title="View"
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        {canInvite(parent) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-1.5 h-9 w-9 rounded-xl flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              openInviteModal(parent);
                            }}
                            aria-label="Invite parent"
                            title="Invite"
                          >
                            <UserPlus className="h-5 w-5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setAddParentErrors({});
        }}
        title="Add Parent"
      >
        <form className="flex flex-col max-h-[90vh] overflow-hidden" onSubmit={handleCreateSubmit}>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
            <Input
              label="First Name"
              required
              placeholder={placeholders.parent_first_name}
              value={parentForm.personal.first_name}
              error={addParentErrors.first_name}
              onChange={(event) => {
                const v = stripDigitsFromName(event.target.value);
                setParentForm((prev) => ({
                  ...prev,
                  personal: { ...prev.personal, first_name: v },
                }));
                if (addParentErrors.first_name) setAddParentErrors((e) => ({ ...e, first_name: '' }));
              }}
            />
            <Input
              label="Last Name"
              placeholder={placeholders.parent_last_name}
              value={parentForm.personal.last_name}
              error={addParentErrors.last_name}
              onChange={(event) => {
                const v = stripDigitsFromName(event.target.value);
                setParentForm((prev) => ({
                  ...prev,
                  personal: { ...prev.personal, last_name: v },
                }));
                if (addParentErrors.last_name) setAddParentErrors((e) => ({ ...e, last_name: '' }));
              }}
            />
            <Input
              label="Email"
              type="email"
              required
              maxLength={EMAIL_MAX_LENGTH}
              placeholder={placeholders.parent_email}
              value={parentForm.personal.email}
              error={addParentErrors.email}
              onChange={(event) => {
                const value = event.target.value.slice(0, EMAIL_MAX_LENGTH);
                setParentForm((prev) => ({
                  ...prev,
                  personal: { ...prev.personal, email: value },
                }));
                const trimmed = value.trim();
                if (trimmed && !validateEmail(trimmed)) {
                  setAddParentErrors((e) => ({ ...e, email: 'Enter a valid email address' }));
                } else {
                  setAddParentErrors((e) => ({ ...e, email: '' }));
                }
              }}
            />
            <div className="w-full">
              <label
                className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1"
                htmlFor="add-parent-phone"
              >
                Phone
              </label>
              <PhoneInput
                country="us"
                value={parentForm.personal.phone?.replace(/^\+/, '') ?? ''}
                onChange={(value) => {
                  setParentForm((prev) => ({
                    ...prev,
                    personal: {
                      ...prev.personal,
                      phone: value ? `+${value}` : '',
                    },
                  }));
                  if (addParentErrors.phone) setAddParentErrors((e) => ({ ...e, phone: '' }));
                }}
                inputStyle={{
                  width: '100%',
                  height: '42px',
                  borderRadius: '0.5rem',
                  borderColor: addParentErrors.phone ? 'var(--error-500, #ef4444)' : undefined,
                }}
                containerStyle={{ width: '100%' }}
                dropdownStyle={{ zIndex: 9999 }}
                buttonStyle={{ borderRadius: '0.5rem 0 0 0.5rem' }}
                inputProps={{ name: 'phone', id: 'add-parent-phone' }}
                containerClass="add-parent-phone-input"
              />
              {addParentErrors.phone && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">{addParentErrors.phone}</p>
              )}
            </div>
            <Textarea
              label="Notes"
              placeholder={placeholders.parent_notes}
              maxLength={NOTES_MAX_LENGTH}
              value={parentForm.notes || ''}
              helperText={`${(parentForm.notes ?? '').length} / ${NOTES_MAX_LENGTH}`}
              onChange={(event) => {
                const value = event.target.value.slice(0, NOTES_MAX_LENGTH);
                setParentForm((prev) => ({ ...prev, notes: value }));
              }}
              rows={3}
            />
          </div>
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 flex justify-end gap-2 py-4">
            <Button variant="ghost" onClick={() => setAddModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isCreating}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} title="Invite Parent">
        <form className="flex flex-col max-h-[90vh] overflow-hidden" onSubmit={handleInviteSubmit}>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
            <Input
              label="Email"
              type="email"
              required
              placeholder={placeholders.invite_email}
              value={inviteForm.email}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
            />
            <Input
              label="Invitee Name"
              required
              placeholder={placeholders.invitee_name}
              value={inviteForm.invitee_name}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, invitee_name: event.target.value }))}
            />

            <Select
              label="Student"
              required
              options={[
                { label: studentsLoading ? 'Loading students...' : placeholders.invite_student, value: '' },
                ...students.map((student) => ({
                  label: `${student.display_name || `${student.first_name || ''} ${student.last_name || ''}`.trim()} (${
                    student.admission_no || student.id
                  })`,
                  value: String(student.id),
                })),
              ]}
              value={inviteForm.student_id}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, student_id: event.target.value }))}
              error={!inviteForm.student_id ? 'Student is required' : undefined}
              disabled={studentsLoading}
            />

            <Select
              label="Relationship"
              options={[
                { label: placeholders.invite_relationship, value: '' },
                { label: 'Mother', value: 'mother' },
                { label: 'Father', value: 'father' },
                { label: 'Guardian', value: 'guardian' },
                { label: 'Other', value: 'other' },
              ]}
              value={inviteForm.metadata?.relationship || ''}
              onChange={(event) =>
                setInviteForm((prev) => ({
                  ...prev,
                  metadata: { ...prev.metadata, relationship: event.target.value },
                }))
              }
            />

            <Textarea
              label="Notes"
              placeholder={placeholders.invite_notes}
              value={inviteForm.notes || ''}
              onChange={(event) => setInviteForm((prev) => ({ ...prev, notes: event.target.value }))}
              rows={3}
            />
          </div>
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 flex justify-end gap-2 py-4">
            <Button variant="ghost" onClick={() => setIsInviteModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isInviting}>
              Send Invite
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(detailParent)}
        onClose={() => setDetailParent(null)}
        title={detailParent?.personal?.display_name || 'Parent details'}
        size="md"
      >
        {detailLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(100vh-200px)] pr-2">
            <dl className="space-y-3 text-sm">
              {detailRows.map((row) => (
                <div key={row.label}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {row.label}
                  </dt>
                  <dd className="mt-1 text-gray-900 dark:text-white break-words">{row.value || '-'}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </Modal>
    </Layout>
  );
};
